/**
 * Single InComm cardholder: incomm_demo merged with the live InComm Cardholder profile.
 *
 * The backend coalesces overlapping fields server-side (live-first, DB fallback) and exposes a
 * ``live_profile_status`` flag so the UI can render a discreet notice when the live call failed.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';

import { Badge } from '@/components/common/Badge';
import { Card, CardBody, CardHeader } from '@/components/common/Card';
import type { Column } from '@/components/common/Table';
import { Table } from '@/components/common/Table';
import type {
  IncommCardDetailResponse,
  LiveWalletItem,
  PurseDetailItem,
  RewardDetailItem,
} from '@/app/services/incommCardService';
import { fetchIncommCardDetail } from '@/app/services/incommCardService';
import {
  cardStatusBadgeVariant,
  formatCardStatusLabel,
} from '@/pages/IncommCards/cardStatusBadgeVariant';

import styles from './IncommCardDetail.module.scss';

function formatMoney(amount: string | null): string {
  if (amount == null) return '—';
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(iso: string | null): string {
  if (iso == null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function Field({ label, value }: { label: string; value: React.ReactNode }): React.ReactElement {
  return (
    <div>
      <div className={styles.dt}>{label}</div>
      <div className={styles.dd}>{value}</div>
    </div>
  );
}

export const IncommCardDetail: React.FC = () => {
  const { eligibleParticipantId } = useParams<{ eligibleParticipantId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<IncommCardDetailResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      if (eligibleParticipantId == null || eligibleParticipantId.trim() === '') {
        setError('Missing participant id');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      setDetail(null);

      try {
        const data = await fetchIncommCardDetail(eligibleParticipantId);
        if (cancelled) return;
        setDetail(data);
      } catch (e) {
        if (cancelled) return;
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          setError('Cardholder not found');
        } else {
          setError(e instanceof Error ? e.message : 'Failed to load cardholder');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [eligibleParticipantId]);

  const liveWalletColumns = useMemo((): Column<LiveWalletItem>[] => {
    return [
      { key: 'display_name', header: 'Wallet', render: (row) => row.display_name ?? row.name ?? '—' },
      {
        key: 'balance',
        header: 'Balance',
        align: 'right',
        render: (row) => formatMoney(row.balance),
      },
      { key: 'status', header: 'Status', render: (row) => row.status ?? '—' },
      { key: 'wallet_type', header: 'Type', render: (row) => row.wallet_type ?? '—' },
    ];
  }, []);

  const purseColumns = useMemo((): Column<PurseDetailItem>[] => {
    return [
      { key: 'purse_name', header: 'Purse' },
      {
        key: 'purse_balance',
        header: 'Balance',
        align: 'right',
        render: (row) => formatMoney(row.purse_balance),
      },
      {
        key: 'effective_date',
        header: 'Effective',
        render: (row) => formatDate(row.effective_date),
      },
      {
        key: 'expiry_date',
        header: 'Expiry',
        render: (row) => formatDate(row.expiry_date),
      },
      {
        key: 'incomm_processed_status',
        header: 'InComm status',
        render: (row) => row.incomm_processed_status ?? '—',
      },
      {
        key: 'incomm_wallet_id',
        header: 'Wallet ID',
        render: (row) => row.incomm_wallet_id ?? '—',
      },
    ];
  }, []);

  const rewardColumns = useMemo((): Column<RewardDetailItem>[] => {
    return [
      { key: 'id', header: 'ID', width: '5rem' },
      {
        key: 'fulfillment_id',
        header: 'Fulfillment',
        render: (row) => <span className={styles.mono}>{row.fulfillment_id}</span>,
      },
      {
        key: 'external_reference',
        header: 'External reference (InComm)',
        render: (row) => <span className={styles.mono}>{row.external_reference}</span>,
      },
      {
        key: 'reward_amount',
        header: 'Amount',
        align: 'right',
        render: (row) => formatMoney(row.reward_amount),
      },
      {
        key: 'incomm_processed_status',
        header: 'InComm status',
        render: (row) => row.incomm_processed_status ?? '—',
      },
      {
        key: 'created_at',
        header: 'Created',
        render: (row) => formatDate(row.created_at),
      },
    ];
  }, []);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading cardholder…</div>
      </div>
    );
  }

  if (error != null || detail == null) {
    return (
      <div className={styles.page}>
        <div className={styles.error} role="alert">
          {error ?? 'Unable to load cardholder'}
        </div>
        <p>
          <Link className={styles.backLink} to="/incomm-cards/list">
            Back to list
          </Link>
        </p>
      </div>
    );
  }

  const liveUnavailable = detail.live_profile_status === 'unavailable';

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div>
          <h1 className={styles.title}>
            Participant {detail.eligible_participant_id}{' '}
            <Badge variant={cardStatusBadgeVariant(detail.card_status)} size="sm">
              {formatCardStatusLabel(detail.card_status)}
            </Badge>
            {detail.enrollment_status != null && detail.enrollment_status !== '' && (
              <>
                {' '}
                <Badge variant="info" size="sm">
                  Enrollment: {detail.enrollment_status}
                </Badge>
              </>
            )}
          </h1>
          <p className={styles.subtitle}>
            {detail.partner_name ?? detail.doing_business_as ?? 'Partner unknown'}
            {detail.partner_product_id != null && ` (#${detail.partner_product_id})`}
          </p>
        </div>
        <Link className={styles.backLink} to="/incomm-cards/list">
          Back to list
        </Link>
      </div>

      {liveUnavailable && (
        <div className={styles.error} role="status" style={{ marginBottom: '1rem' }}>
          Live InComm profile unavailable — showing last enriched values from incomm_demo.
        </div>
      )}

      <div className={styles.sectionStack}>
        <Card>
          <CardHeader
            title="Card"
            subtitle={
              liveUnavailable
                ? 'incomm_demo (live InComm Cardholder profile unavailable)'
                : 'incomm_demo merged with live InComm Cardholder profile (live-first, DB fallback)'
            }
          />
          <CardBody>
            <div className={styles.dlGrid}>
              <Field label="Card last4" value={detail.card_last4 ?? '—'} />
              <Field label="Wallet balance" value={formatMoney(detail.wallet_balance)} />
              <Field label="Shipped" value={formatDate(detail.shipped_date)} />
              <Field label="E-card" value={detail.e_card == null ? '—' : detail.e_card ? 'Yes' : 'No'} />
              <Field label="Health plan" value={detail.health_plan_code} />
              <Field label="Health plan name" value={detail.health_plan_name ?? '—'} />
              <Field label="Program" value={detail.program_code} />
              <Field label="Program name" value={detail.program_name ?? '—'} />
              <Field label="Package" value={detail.package_id} />
              {detail.live_card != null && (
                <>
                  <Field
                    label="Card display number"
                    value={detail.live_card.card_display_number ?? '—'}
                  />
                  <Field label="Serial number" value={detail.live_card.serial_number} />
                  <Field label="Card type" value={detail.live_card.card_type ?? '—'} />
                  <Field label="Issue date" value={formatDate(detail.live_card.issue_date_utc)} />
                  <Field
                    label="Expiration"
                    value={formatDate(detail.live_card.expiration_date_utc)}
                  />
                  <Field
                    label="Shipment tracking"
                    value={detail.live_card.shipment_tracking_number ?? '—'}
                  />
                  <Field
                    label="Delivery method"
                    value={detail.live_card.shipment_delivery_method ?? '—'}
                  />
                  <Field
                    label="Scan-to-pay"
                    value={detail.live_card.scan_to_pay_code ?? '—'}
                  />
                </>
              )}
            </div>
          </CardBody>
        </Card>

        <div data-testid="incomm-identifiers-section">
          <Card>
            <CardHeader
              title="InComm Identifiers"
              subtitle="What InComm has on file for this cardholder. Legacy rows (pseudonym_key_version=0) hold raw Lore IDs; new rows (>=1) hold base32 HMAC pseudonyms."
            />
            <CardBody>
              <div className={styles.dlGrid}>
                <Field
                  label="External Member ID"
                  value={
                    <span data-testid="incomm-external-member-id" className={styles.mono}>
                      {detail.external_member_id}
                    </span>
                  }
                />
                <Field
                  label="External Member ID 2"
                  value={
                    <span data-testid="incomm-external-member-id-2" className={styles.mono}>
                      {detail.external_member_id_2 ?? '—'}
                    </span>
                  }
                />
                <Field
                  label="External Group ID"
                  value={
                    <span data-testid="incomm-external-group-id" className={styles.mono}>
                      {detail.external_group_id ?? '—'}
                    </span>
                  }
                />
                <Field
                  label="Pseudonym key version"
                  value={
                    <span data-testid="incomm-pseudonym-key-version">
                      {detail.pseudonym_key_version}
                    </span>
                  }
                />
              </div>
            </CardBody>
          </Card>
        </div>

        <Card>
          <CardHeader
            title="Demographics & address"
            subtitle="Live InComm Cardholder profile, falling back to incomm_demo. Email, phone, and gender are intentionally omitted."
          />
          <CardBody>
            <div className={styles.dlGrid}>
              <Field
                label="Name"
                value={
                  [detail.first_name, detail.middle_name, detail.last_name]
                    .filter(Boolean)
                    .join(' ') || '—'
                }
              />
              <Field label="DOB" value={formatDate(detail.date_of_birth)} />
              <Field
                label="Address"
                value={
                  [detail.address_line1, detail.address_line2].filter(Boolean).join(', ') || '—'
                }
              />
              <Field
                label="City / State / ZIP"
                value={
                  [detail.city, detail.state, detail.zip_code].filter(Boolean).join(', ') || '—'
                }
              />
              <Field label="Group number" value={detail.group_number ?? '—'} />
              <Field label="Member no. 2" value={detail.member_no_2 ?? '—'} />
              <Field label="MBI" value={detail.mbi ?? '—'} />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Enrollment"
            subtitle="Top-level cardholder profile status (separate from card lifecycle)."
          />
          <CardBody>
            <div className={styles.dlGrid}>
              <Field label="Enrollment status" value={detail.enrollment_status ?? '—'} />
              <Field
                label="Enrollment status ID"
                value={detail.enrollment_status_id ?? '—'}
              />
              <Field label="Enrollment start" value={formatDate(detail.enrollment_start_utc)} />
              <Field label="Enrollment end" value={formatDate(detail.enrollment_end_utc)} />
            </div>
          </CardBody>
        </Card>

        {detail.live_wallets.length > 0 && (
          <Card>
            <CardHeader
              title="Wallets on card"
              subtitle="Live wallet balances from the InComm Cardholder profile."
            />
            <CardBody>
              <Table
                columns={liveWalletColumns}
                data={detail.live_wallets}
                keyExtractor={(row) => String(row.wallet_id)}
                emptyMessage="No wallets"
              />
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader title="InComm sync" />
          <CardBody>
            <div className={styles.dlGrid}>
              <Field label="Processed status" value={detail.incomm_processed_status ?? '—'} />
              <Field label="Processed at" value={formatDate(detail.incomm_processed_at)} />
              <Field label="Sent at" value={formatDate(detail.sent_to_incomm_at)} />
              <Field label="Sent filename" value={detail.sent_to_incomm_filename ?? '—'} />
              <Field label="udf_1 (partner product id string)" value={detail.udf_1 ?? '—'} />
              <Field label="udf_2" value={detail.udf_2 ?? '—'} />
              <Field label="Last profile enriched" value={formatDate(detail.last_profile_enriched_at)} />
            </div>
            {detail.incomm_return_info != null && Object.keys(detail.incomm_return_info).length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <div className={styles.dt}>Return info (JSON)</div>
                <pre className={styles.preJson}>
                  {JSON.stringify(detail.incomm_return_info, null, 2)}
                </pre>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Purses" subtitle={`${detail.purses.length} row(s)`} />
          <CardBody>
            {detail.purses.length === 0 ? (
              <p className={styles.subtitle}>No purses.</p>
            ) : (
              <Table
                columns={purseColumns}
                data={detail.purses}
                keyExtractor={(row) => `${row.eligible_participant_id}-${row.purse_name}`}
                emptyMessage="No purses"
              />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Purse rewards" subtitle={`${detail.rewards.length} row(s)`} />
          <CardBody>
            {detail.rewards.length === 0 ? (
              <p className={styles.subtitle}>No purse rewards.</p>
            ) : (
              <Table
                columns={rewardColumns}
                data={detail.rewards}
                keyExtractor={(row) => String(row.id)}
                emptyMessage="No rewards"
              />
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default IncommCardDetail;
